import moment from 'moment'
import { instance as store } from 'app/store'
import { getJoinedChannel, getClientId } from 'app/utils/data/socket'

const initialState = {
  isProcessingWebDownload: false,
  isProcessingDesktopDownload: false,
  isProcessingNpmPackage: false,
  kitDownloadError: false,
  kitNpmPackageError: false,
  kitDownloadCompleted: false,
  kitNpmPackageCompleted: false,
  kitNpmPackageLastUpdatedAt: null
}

// Ignore any event messages for kits that aren't the one we're working on.
// Also ignore if the clientId doesn't match: it means some other browser logged into
// this account initiated the job. We want this browser to process only notifications
// for jobs it started.
function messageIsForMe(messageData, { kitToken, clientId, workerName }) {
  if (!kitToken) return false
  if (!workerName) return false

  if (messageData?.kitToken !== kitToken) {
    return false
  }

  if ('kit_npm_package' === workerName) {
    // For npm package jobs, we don't care about clientId matching.
    // All clients logged into the account should get the notification.
    return true
  } else {
    // If it's not an npm package job, then we also want the clientId to match,
    // so kit downloads are only completed in the browser that initiated them.
    if (!clientId) return false
    return messageData?.clientId === clientId
  }
}

async function handleKitJob({ commit }, actionParams) {
  for (const param of ['kitToken', 'workerName']) {
    if ('string' !== typeof actionParams?.[param]) throw new Error(`Missing required parameter: ${param}`)
  }

  const isKitDownload = actionParams.workerName === 'kit_download'

  if (isKitDownload && 'string' !== typeof actionParams?.buildType) throw new Error('Missing required parameter: buildType')

  const { kitToken, buildType, workerName } = actionParams

  const clientId = getClientId()

  return new Promise(function (resolve, reject) {
    const initialStateTransitionParams = {}

    if (isKitDownload) {
      initialStateTransitionParams.isProcessingWebDownload = buildType === 'web'
      initialStateTransitionParams.isProcessingDesktopDownload = buildType === 'desktop'
      initialStateTransitionParams.kitDownloadError = false
      initialStateTransitionParams.kitDownloadCompleted = false
    } else {
      initialStateTransitionParams.isProcessingNpmPackage = true
      initialStateTransitionParams.kitNpmPackageCompleted = false
      initialStateTransitionParams.kitNpmPackageError = false
      initialStateTransitionParams.kitNpmPackageLastUpdatedAt = null
    }

    commit('transition', initialStateTransitionParams)
    const callbackRefs = []

    const errorCallback = (error, channel) => {
      const errorStateTransitionParams = {}
      if (isKitDownload) {
        errorStateTransitionParams.isProcessingWebDownload = false
        errorStateTransitionParams.isProcessingDesktopDownload = false
        errorStateTransitionParams.kitDownloadError = true
        errorStateTransitionParams.kitDownloadCompleted = false
      } else {
        errorStateTransitionParams.isProcessingNpmPackage = false
        errorStateTransitionParams.kitNpmPackageCompleted = false
        errorStateTransitionParams.kitNpmPackageError = true
      }
      commit('transition', errorStateTransitionParams)
      if (channel) {
        channel.off(callbackRefs)
      }
      const e = 'string' === typeof error ? new Error(error) : error
      reject(e)
    }

    getJoinedChannel('/jobs', `kit-jobs:${store.getters['accounts/currentAccount']?.id}`)
      .then((channel) => {
        const refComplete = channel.on(`complete_${workerName}`, (data) => {
          if (!messageIsForMe(data, { kitToken, clientId, workerName })) {
            return
          }

          const completionStateTransitionParams = {}

          if (isKitDownload) {
            completionStateTransitionParams.isProcessingWebDownload = false
            completionStateTransitionParams.isProcessingDesktopDownload = false
            completionStateTransitionParams.kitDownloadError = false
            completionStateTransitionParams.kitDownloadCompleted = true
          } else {
            completionStateTransitionParams.isProcessingNpmPackage = false
            completionStateTransitionParams.kitNpmPackageCompleted = true
            completionStateTransitionParams.kitNpmPackageError = false
            completionStateTransitionParams.kitNpmPackageLastUpdatedAt = moment.now()
          }

          commit('transition', completionStateTransitionParams)
          channel.off(callbackRefs)
          resolve({ downloadUrl: data.downloadUrl })
        })

        const refTimeout = channel.on(`timeout_${workerName}`, (data) => {
          if (messageIsForMe(data, { kitToken, clientId, workerName })) {
            errorCallback(`timeout_${workerName}`, channel)
          }
        })

        const refFailed = channel.on(`failed_${workerName}`, (data) => {
          if (messageIsForMe(data, { kitToken, clientId, workerName })) {
            errorCallback(`failed_${workerName}`, channel)
          }
        })

        const refError = channel.onError(() => {
          errorCallback('channel error', channel)
        })

        // Add all of these callback refs so they can all be cleaned up
        // when we're done with this use of the channel. If this function
        // is called again, it will add new callbacks for the channel events.
        // Otherwise, each time this function is invoked, the callbacks get
        // added *in addition* to the callbacks added in earlier invocations.
        // We don't want multiple callbacks being invoked per event on subsequent
        // invocations of this function.
        callbackRefs.splice(0, -1, refComplete, refTimeout, refError, refFailed)

        channel.push(`build_${workerName}`, { kitToken, buildType, clientId }).receive('error', (e) => errorCallback(`build_${workerName}: ${e}`, channel))
      })
      .catch(errorCallback)
  })
}

export default {
  namespaced: true,

  state: {
    ...initialState
  },

  getters: {
    isProcessingWebDownload: (state) => state.isProcessingWebDownload,
    isProcessingDesktopDownload: (state) => state.isProcessingDesktopDownload,
    isProcessingDownload: (state) => state.isProcessingWebDownload || state.isProcessingDesktopDownload,
    isProcessingNpmPackage: (state) => state.isProcessingNpmPackage,
    kitDownloadCompleted: (state) => state.kitDownloadCompleted,
    kitNpmPackageCompleted: (state) => state.kitNpmPackageCompleted,
    kitNpmPackageLastUpdatedAt: (state) => state.kitNpmPackageLastUpdatedAt,
    kitDownloadError: (state) => state.kitDownloadError,
    kitNpmPackageError: (state) => state.kitNpmPackageError
  },

  mutations: {
    transition(state, transitions) {
      Object.keys(transitions).forEach((key) => {
        state[key] = transitions[key]
      })
    },

    resetState(state) {
      Object.assign(state, { ...initialState })
    }
  },

  actions: {
    async buildKitForDownload(storeParams, actionParams) {
      return await handleKitJob(storeParams, { ...actionParams, workerName: 'kit_download' })
    },

    async uploadPackage(storeParams, kitToken) {
      return await handleKitJob(storeParams, { kitToken, workerName: 'kit_npm_package' })
    }
  }
}
